import { useCustom } from "@refinedev/core";
import type { AddonDetail, QuizPreparation } from "types/";
import { ANALYTICS_STALE_TIME } from "../../constants/constants";
import { buildVersionFilters } from "../../utils/";
import type { AddonKey } from "../utils/metrics";

const addonsBase = (projectId: string | undefined) =>
  `analytics/projects/${projectId}/analytics/addons`;

/**
 * Fetches one add-on's detail from GET {base}/{addon}/detail, for `version`
 * (undefined: the live one). Errors are silenced: the page shows its own error card.
 */
export const useAddonDetail = (
  projectId: string | undefined,
  addon: AddonKey,
  version: string | undefined,
  enabled: boolean
) =>
  useCustom<AddonDetail>({
    url: `${addonsBase(projectId)}/${addon}/detail`,
    method: "get",
    config: { filters: buildVersionFilters(version) },
    queryOptions: { enabled, staleTime: ANALYTICS_STALE_TIME },
    errorNotification: false,
  });

/**
 * Fetches the quiz preparation from GET {base}/quiz/preparation, for `version`
 * (undefined: the live one). Errors are silenced: the page shows its own error card.
 */
export const useQuizPreparation = (
  projectId: string | undefined,
  version: string | undefined,
  enabled: boolean
) => {
  const query = useCustom<QuizPreparation>({
    url: `${addonsBase(projectId)}/quiz/preparation`,
    method: "get",
    config: { filters: buildVersionFilters(version) },
    queryOptions: { enabled, staleTime: ANALYTICS_STALE_TIME },
    errorNotification: false,
  });
  const preparation = query.data?.data ?? null;
  return {
    preparation,
    preparationLoading: query.isInitialLoading,
    // A failed request must not read as "no data": the panels say so instead.
    preparationFailed: query.isError && !preparation,
  };
};
